import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";

import { FilterAutocomplete, Table } from "../../../../components/ui";
import { WITHDRAWALS_COLUMNS } from "./constants";
import { fundedComplianceActions } from "../../../../reducers/FundedCompliance";
import { FUNDED_COMPLIANCE_API_URLS, FundedComplianceAPI } from "../../../../api/fundedCompliance";
import usePagination from "../../../../hooks/usePagination";
import { appendSearchParamsToQuery } from "../../../ComplianceSearch/utils";
import { ISortBy } from "../../../../types";
import newSortBy from "../../../../utils/newSortBy";
import ComplianceStatusSelect from "./components/ComplianceStatusSelect";

export const WITHDRAWALS_FILTER_OPTIONS = {
  Name: "names",
  Email: "emails",
  "Entries id": "compliance_entries_id",
  "Payout day": "payout_day",
};

const Withdrawals: React.FC = () => {
  const dispatch = useDispatch();
  const pagination = usePagination();

  const { withdrawals } = useSelector((state) => state.fundedCompliance);

  const [complianceStatus, setComplianceStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<ISortBy>({ by: "id", order: false });
  const [search, setSearch] = useState<{
    Name: string;
    Email: string;
    "Entries id": string;
    "Payout day": string;
  }>({
    Name: "",
    Email: "",
    "Entries id": "",
    "Payout day": "",
  });

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  const { isError, error } = useQuery({
    queryKey: [
      FUNDED_COMPLIANCE_API_URLS.withdrawals,
      pagination.page,
      pagination.limit,
      search,
      sortBy,
      complianceStatus,
    ],
    queryFn: async ({ signal }) => {
      const query = new URLSearchParams();
      query.set("page", pagination.page.toString());
      query.set("limit", pagination.limit.toString());
      query.append("sortBy", sortBy.by);
      query.append("order", sortBy.order ? "ASC" : "DESC");
      query.append("compliance_status", complianceStatus);

      appendSearchParamsToQuery({ ...search }, query);

      dispatch(fundedComplianceActions.fetchWithdrawalsRequest());
      const response = await FundedComplianceAPI.getWithdrawalsRequest(query.toString(), signal);
      dispatch(fundedComplianceActions.fetchWithdrawalsSuccess(response.data));
      pagination.setCount(response.data.withdrawals.count);
      return response;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(fundedComplianceActions.fetchWithdrawalsFailed());
    }
  }, [isError, error]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={10}>
          <FilterAutocomplete
            search={search}
            setSearch={setSearch}
            filters={WITHDRAWALS_FILTER_OPTIONS}
            inputTypes={{
              "Payout day": "date",
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <ComplianceStatusSelect
            setComplianceStatus={setComplianceStatus}
            complianceStatus={complianceStatus}
          />
        </Grid>
      </Grid>

      <Box sx={{ table: { height: "100%", fontSize: "12px" } }}>
        <Table
          theadTitle="Withdrawals"
          csv_table_name="Withdrawals"
          loading={withdrawals.isLoading}
          data={withdrawals.data.rows}
          columns={WITHDRAWALS_COLUMNS}
          extractKey={(row) => row.id}
          pagination={pagination}
          handleSortBy={handleSortBy}
          sortBy={sortBy}
        />
      </Box>
    </div>
  );
};

export default Withdrawals;
